import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import { STEP_TESTS_FEATURE_FLAG } from "../../shared/featureFlagNames";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import {
  computeStepTestFailures,
  STEP_TEST_TIMEOUT_MS,
  type TestExecution,
} from "../lib/stepTestFailures";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { Text } from "./Text";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 420px;
  max-width: 90vw;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FailureList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 320px;
  overflow-y: auto;
`;

const StepGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SpecRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 18px;
`;

const Message = styled("div")`
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type Props = {
  workflowId: string;
  branchId: string;
  onProceed: () => void;
  onCancel: () => void;
};

export function PushLivePreflight({
  workflowId,
  branchId,
  onProceed,
  onCancel,
}: Props) {
  const z = useZero();
  const [flags, flagsResult] = useQuery(queries.featureFlags());
  const stepTestsEnabled = flags.some(
    (f) => f.name === STEP_TESTS_FEATURE_FLAG
  );

  // Read the branch's head commit from the same query the workflow page uses,
  // so the files (and the commit id we run examples against) are the live head.
  const [branch, branchResult] = useQuery(
    queries.loadBranch({ workflowId, branchId })
  );
  const headCommit = branch?.headCommit ?? null;
  const commitId = headCommit?.id ?? null;
  const testStepDirs = useMemo(
    () => stepDirsWithStepTests(headCommit?.files ?? []),
    [headCommit?.files]
  );

  const [runs, setRuns] = useState<
    { stepDir: string; executionId: string }[] | null
  >(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedRef = useRef(false);
  const proceededRef = useRef(false);

  // Once the branch's files have loaded, kick off a fresh step-test run per step
  // that has examples. No examples → nothing to gate, push straight through.
  useEffect(() => {
    if (startedRef.current || flagsResult.type !== "complete") return;
    if (!stepTestsEnabled) {
      startedRef.current = true;
      onProceed();
      return;
    }
    if (branchResult.type !== "complete") return;
    startedRef.current = true;
    if (!commitId || testStepDirs.length === 0) {
      onProceed();
      return;
    }
    setRuns(
      testStepDirs.map((stepDir) => {
        const executionId = nanoid(12);
        z.mutate(
          sharedMutators.runStepTests({
            runId: nanoid(12),
            workflowId,
            commitId,
            stepDir,
            executionId,
          })
        );
        return { stepDir, executionId };
      })
    );
  }, [
    branchResult.type,
    flagsResult.type,
    stepTestsEnabled,
    testStepDirs,
    commitId,
    z,
    workflowId,
    onProceed,
  ]);

  useEffect(() => {
    if (!runs) return;
    const t = setTimeout(() => setTimedOut(true), STEP_TEST_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [runs]);

  const { executions: testExecutions } = useStepTestExecutions(
    workflowId,
    branchId
  );
  const byId = useMemo(() => {
    const map = new Map<string, TestExecution>();
    for (const e of testExecutions) map.set(e.id, e);
    return map;
  }, [testExecutions]);

  const execs = runs?.map((r) => byId.get(r.executionId)) ?? [];
  const total = runs?.length ?? 0;
  const completed = execs.filter((e) => e && e.status !== "pending").length;
  const allTerminal = runs != null && completed === total;

  // Spec rows commit in the same transaction as the execution's terminal
  // status, so once every run is terminal we can judge immediately.
  const failures =
    runs && (allTerminal || timedOut)
      ? computeStepTestFailures(runs, byId, timedOut)
      : null;

  useEffect(() => {
    if (proceededRef.current) return;
    if (failures && failures.length === 0) {
      proceededRef.current = true;
      onProceed();
    }
  }, [failures, onProceed]);

  if (failures == null || failures.length === 0) {
    return (
      <Modal open onClose={onCancel}>
        <Content style={{ width: 320 }}>
          <Row>
            <Icon name="list-todo" size={16} color="var(--text-secondary)" />
            <Text variant="text-regular-md">Checking step tests…</Text>
          </Row>
          <Text variant="text-small" color="var(--text-secondary)">
            {total > 0
              ? `Checking ${total} step${total === 1 ? "" : "s"} before pushing live (${completed}/${total} done).`
              : "Checking this workflow before pushing live."}
          </Text>
          <Footer>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </Footer>
        </Content>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onCancel}>
      <Content>
        <Text variant="text-regular-md">Some step tests failed</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          These step tests didn’t pass on the latest version. Push live anyway?
        </Text>
        <FailureList>
          {failures.map((f) => (
            <StepGroup key={f.stepDir}>
              <Row>
                <Icon name="x" size={12} color="var(--red-600)" />
                <Text variant="text-small-md">{f.stepDir}</Text>
              </Row>
              {f.timedOut ? (
                <Text variant="text-small" color="var(--text-secondary)">
                  Step tests didn’t finish in time.
                </Text>
              ) : f.specs.length === 0 ? (
                <Text variant="text-small" color="var(--text-secondary)">
                  Step tests could not run.
                </Text>
              ) : (
                f.specs.map((s) => (
                  <SpecRow key={s.name}>
                    <Text variant="text-small">{s.name}</Text>
                    {s.message && (
                      <Message>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {s.message}
                        </Text>
                      </Message>
                    )}
                  </SpecRow>
                ))
              )}
            </StepGroup>
          ))}
        </FailureList>
        <Footer>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onProceed}>Push live anyway</Button>
        </Footer>
      </Content>
    </Modal>
  );
}
